/**
 * Adapter functions that convert AgentMessagePart variants (from agent-messages.ts)
 * into the prop interfaces expected by the new Cloudscape-native chat components.
 *
 * Each adapter is a pure function: AgentMessagePart -> ComponentProps.
 */

import type {
  ToolStep,
  AgentMessagePart,
  AccessGroup,
  AccessTableEntry,
  ActionButtonDef,
} from '../../types/agent-messages';

import type { ThinkingStep, ThinkingPanelProps } from './ThinkingPanel';
import type { CodeBlockProps, CodeBlockMetadata } from './CodeBlock';
import type {
  AccessStatusTableProps,
  AccessSystemGroup,
  AccessTableInfo,
} from './AccessStatusTable';
import type { ActionCardProps, ActionButton } from './ActionCard';
import type { OutputPreviewProps, OutputColumn } from './OutputPreview';
import type { NotificationBannerProps } from './NotificationBanner';
import type { FormPanelProps, TableInfo, DynamicQuestion } from './FormPanel';
import type { RequirementReviewCardProps, RequirementButton } from './RequirementReviewCard';
import type { JobDashboardProps, DashboardButton, JobEntry } from './JobDashboard';
import type { ArtifactReviewCardProps, ArtifactButton } from './ArtifactReviewCard';

// ---------------------------------------------------------------------------
// ThinkingPanel: ToolStep[] -> ThinkingStep[]
// ---------------------------------------------------------------------------

function mapToolStepStatus(
  status: ToolStep['status'],
): ThinkingStep['status'] {
  switch (status) {
    case 'running':
      return 'in_progress';
    case 'complete':
      return 'complete';
    case 'error':
      return 'error';
    default:
      return 'in_progress';
  }
}

export function toolStepToThinkingStep(step: ToolStep): ThinkingStep {
  const result: ThinkingStep = {
    tool_name: step.displayName || step.toolName,
    status: mapToolStepStatus(step.status),
  };

  if (step.output != null) {
    result.result_summary =
      typeof step.output === 'string'
        ? step.output
        : JSON.stringify(step.output).slice(0, 200);
  }

  return result;
}

export function toThinkingPanelProps(
  part: Extract<AgentMessagePart, { type: 'thinking' }>,
  isStreaming?: boolean,
): Omit<ThinkingPanelProps, 'expandable'> {
  return {
    steps: part.steps.map(toolStepToThinkingStep),
    duration: part.duration,
    isStreaming: isStreaming ?? false,
  };
}

// ---------------------------------------------------------------------------
// CodeBlock: codeBlock part -> CodeBlockProps
// ---------------------------------------------------------------------------

export function toCodeBlockProps(
  part: Extract<AgentMessagePart, { type: 'codeBlock' }>,
): Omit<CodeBlockProps, 'onCopy' | 'onSave' | 'onDownload' | 'onExecute'> {
  const metadata: CodeBlockMetadata | undefined = part.metadata
    ? {
        tables: part.metadata.tables,
        estimatedRows: part.metadata.estRows,
      }
    : undefined;

  return {
    language: part.language as CodeBlockProps['language'],
    code: part.code,
    actions: ['copy', 'save', 'download'],
    metadata,
  };
}

// ---------------------------------------------------------------------------
// AccessStatusTable: AccessGroup[] -> AccessSystemGroup[]
// ---------------------------------------------------------------------------

function mapAccessTableEntry(entry: AccessTableEntry): AccessTableInfo {
  const info: AccessTableInfo = {
    name: entry.name,
    status: entry.status,
  };

  if (entry.checks) {
    info.checks = entry.checks;
  }

  if (entry.issues) {
    info.issues = entry.issues;
  }

  if (entry.action) {
    info.action = {
      type: 'deeplink',
      label: 'Request Access',
      url: entry.action,
    };
  }

  return info;
}

function mapAccessGroup(group: AccessGroup): AccessSystemGroup {
  return {
    system_name: group.systemName,
    tables: group.tables.map(mapAccessTableEntry),
  };
}

export function toAccessStatusProps(
  part: Extract<AgentMessagePart, { type: 'accessStatus' }>,
): AccessStatusTableProps {
  return {
    groups: part.groups.map(mapAccessGroup),
    summary: part.summary,
    degraded: part.degraded,
    degraded_warning: part.degraded_warning,
  };
}

// ---------------------------------------------------------------------------
// ActionCard: ActionButtonDef[] -> ActionButton[]
// ---------------------------------------------------------------------------

function mapActionButton(
  btn: ActionButtonDef,
  _onAction?: (action: string) => void,
): ActionButton {
  return {
    label: btn.label,
    style: btn.style,
    action: btn.action,
    // NOTE: onClick intentionally omitted — onButtonClick on the card handles dispatch.
  };
}

export function toActionCardProps(
  part: Extract<AgentMessagePart, { type: 'actionCard' }>,
  onAction?: (action: string) => void,
): ActionCardProps {
  return {
    title: part.title,
    description: part.description,
    buttons: part.buttons.map((b) => mapActionButton(b, onAction)),
    onButtonClick: onAction,
  };
}

// ---------------------------------------------------------------------------
// OutputPreview: string[] columns -> OutputColumn[]
// ---------------------------------------------------------------------------

export function toOutputPreviewProps(
  part: Extract<AgentMessagePart, { type: 'outputPreview' }>,
): OutputPreviewProps {
  const columns: OutputColumn[] = part.columns.map((col) => ({
    id: col,
    header: col,
  }));

  const rows = part.rows.map((row) => {
    const obj: Record<string, unknown> = {};
    part.columns.forEach((col, i) => {
      obj[col] = row[i];
    });
    return obj;
  });

  return {
    columns,
    rows,
    totalEstimate: part.totalEstimate,
    queryTime: part.queryTime ? parseFloat(part.queryTime) : undefined,
  };
}

// ---------------------------------------------------------------------------
// NotificationBanner: notification part -> NotificationBannerProps
// ---------------------------------------------------------------------------

export function toNotificationProps(
  part: Extract<AgentMessagePart, { type: 'notification' }>,
  onAction?: (action: string) => void,
): NotificationBannerProps {
  return {
    variant: part.variant,
    title: part.title,
    description: part.description,
    action: part.action
      ? {
          label: part.action.label,
          onClick: onAction
            ? () => onAction(part.action!.action)
            : () => {},
        }
      : undefined,
  };
}

// ---------------------------------------------------------------------------
// FormPanel: AccessFormData -> FormPanelProps
// ---------------------------------------------------------------------------

export function toFormPanelProps(
  part: Extract<AgentMessagePart, { type: 'form' }>,
  onSubmit?: (formData: Record<string, any>) => void,
  onCancel?: () => void,
): FormPanelProps {
  const fd = part.formData;

  const tables: TableInfo[] = fd.tables.map((t) => ({
    name: t.name,
    owner: t.owner,
    classification: t.classification,
  }));

  const dynamicQuestions: DynamicQuestion[] = fd.questions.map((q) => ({
    id: q.id,
    question: q.label,
    type: q.type,
    required: q.required,
    options: q.options?.map((o) => ({ label: o, value: o })),
    placeholder: undefined,
  }));

  return {
    title: fd.title,
    tables,
    consumer: fd.consumer,
    teamName: fd.teamName,
    businessJustification: fd.businessJustification,
    additionalInformation: fd.additionalInformation,
    dynamicQuestions,
    onSubmit,
    onCancel,
  };
}

// ---------------------------------------------------------------------------
// RequirementReviewCard: requirementReview part -> RequirementReviewCardProps
// ---------------------------------------------------------------------------

export function toRequirementReviewProps(
  part: Extract<AgentMessagePart, { type: 'requirementReview' }>,
  onAction?: (action: string) => void,
): RequirementReviewCardProps {
  const buttons: RequirementButton[] = part.buttons.map((btn) => ({
    label: btn.label,
    style: btn.style,
    action: btn.action,
    // NOTE: onClick intentionally omitted — onButtonClick on the card handles dispatch.
  }));

  return {
    title: part.title,
    useCaseId: part.useCaseId,
    objective: part.objective,
    status: part.status,
    sections: part.sections,
    buttons,
    onButtonClick: onAction,
  };
}

// ---------------------------------------------------------------------------
// JobDashboard: jobDashboard part -> JobDashboardProps
// ---------------------------------------------------------------------------

export function toJobDashboardProps(
  part: Extract<AgentMessagePart, { type: 'jobDashboard' }>,
  onAction?: (action: string) => void,
): JobDashboardProps {
  const buttons: DashboardButton[] = (part.buttons ?? []).map((btn) => ({
    label: btn.label,
    style: btn.style,
    action: btn.action,
    // NOTE: onClick intentionally omitted — onButtonClick on the card handles dispatch.
  }));

  const jobs: JobEntry[] = part.jobs.map((j) => ({
    jobId: j.jobId,
    name: j.name,
    status: j.status,
    lastRun: j.lastRun,
    jobType: j.jobType,
  }));

  return {
    jobs,
    summary: part.summary,
    buttons,
    onButtonClick: onAction,
  };
}

// ---------------------------------------------------------------------------
// ArtifactReviewCard: artifactReview part -> ArtifactReviewCardProps
// ---------------------------------------------------------------------------

export function toArtifactReviewProps(
  part: Extract<AgentMessagePart, { type: 'artifactReview' }>,
  onAction?: (action: string) => void,
): ArtifactReviewCardProps {

  const wrappedOnAction = onAction
    ? (action: string) => {
        if (action === 'approve_artifacts') {
          onAction(`approve_artifacts:${part.jobId}`);
        } else {
          onAction(action);
        }
      }
    : undefined;

  const buttons: ArtifactButton[] = part.buttons.map((btn) => ({
    label: btn.label,
    style: btn.style,
    action: btn.action,
    // NOTE: onClick intentionally omitted — onButtonClick on the card handles dispatch.
    // Setting both causes handleClick() to fire the action twice.
  }));

  return {
    jobId: part.jobId,
    jobName: part.jobName,
    generateStatus: part.generateStatus,
    sections: part.sections,
    buttons,
    onButtonClick: wrappedOnAction,
  };
}

// ---------------------------------------------------------------------------
// SuggestedActions: suggestedActions part -> SuggestedActionsProps
// ---------------------------------------------------------------------------

export function toSuggestedActionsProps(
  part: Extract<AgentMessagePart, { type: 'suggestedActions' }>,
  onAction?: (action: string) => void,
): { actions: typeof part.actions; onActionClick?: (action: string) => void } {
  return {
    actions: part.actions,
    onActionClick: onAction,
  };
}
